import { DEDUP_CACHE_SERVICES, DEDUP_CACHE_DEFAULT_TTL } from './constants'

// Default deduplication-cache form state, with every service key present so
// inputs are never driven by `undefined`.
export const getDedupCacheDefaultState = () => ({
  enabled: false,
  ttl: DEDUP_CACHE_DEFAULT_TTL,
  services: DEDUP_CACHE_SERVICES.reduce((acc, { key }) => {
    acc[key] = { enabled: false, ttl: DEDUP_CACHE_DEFAULT_TTL }
    return acc
  }, {})
})

// Merge the checked property value (response.data.value) over the defaults so the
// form always has a complete, well-shaped value even if the backend omits some services.
export const normalizeDedupCacheResponse = (data) => {
  const defaults = getDedupCacheDefaultState()
  if (!data) {
    return defaults
  }

  // System (global) ttl is the fallback for any service whose ttl the check
  // API omits; the global ttl itself falls back to the built-in default.
  const systemTtl = data.ttl != null ? data.ttl : defaults.ttl

  return {
    enabled: Boolean(data.enabled),
    ttl: systemTtl,
    services: DEDUP_CACHE_SERVICES.reduce((acc, { key }) => {
      const service = (data.services && data.services[key]) || {}
      acc[key] = {
        enabled: Boolean(service.enabled),
        ttl: service.ttl != null ? service.ttl : systemTtl
      }
      return acc
    }, {})
  }
}

// Build the `values` payload for the update API from the form state,
// coercing all ttl inputs to numbers.
export const buildDedupCacheValues = (state) => ({
  enabled: Boolean(state.enabled),
  ttl: Number(state.ttl),
  services: DEDUP_CACHE_SERVICES.reduce((acc, { key }) => {
    const service = state.services[key] || {}
    acc[key] = {
      enabled: Boolean(service.enabled),
      ttl: Number(service.ttl)
    }
    return acc
  }, {})
})

// A ttl is valid when it is a non-negative finite integer.
export const isValidTtl = (value) => {
  const num = Number(value)
  return Number.isFinite(num) && Number.isInteger(num) && num >= 0
}

// Validate every ttl in the form state (global + per-service).
export const hasValidTtls = (state) => {
  if (!isValidTtl(state.ttl)) {
    return false
  }
  return DEDUP_CACHE_SERVICES.every(({ key }) => isValidTtl(state.services[key].ttl))
}

// Format a ttl (in seconds) as a readable duration, e.g. 90061 -> "1 day 1 hour 1 minute 1 second".
// Returns '' for empty/invalid input so callers can skip rendering.
export const formatTtl = (seconds) => {
  const total = Math.floor(Number(seconds))
  if (!Number.isFinite(total) || total < 0) {
    return ''
  }
  if (total === 0) {
    return '0 seconds'
  }

  const units = [
    { label: 'day', secs: 86400 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
    { label: 'second', secs: 1 }
  ]

  let remaining = total
  const parts = units.reduce((acc, { label, secs }) => {
    const value = Math.floor(remaining / secs)
    if (value > 0) {
      acc.push(`${value} ${label}${value > 1 ? 's' : ''}`)
      remaining -= value * secs
    }
    return acc
  }, [])

  return parts.join(' ')
}
